// /login page.
//
// Pure client component -- submits credentials to POST /api/auth/login,
// which sets the HttpOnly session cookie. On success we full-redirect via
// window.location so middleware re-runs and any server components on the
// next page see the cookie immediately.

"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { ApiClientError, postLogin } from "@/lib/api";

/** Validate that a redirect target is a safe relative path on this origin. */
function safeNext(raw: string | null): string {
  if (!raw) return "/projects";
  // Must be relative (starts with /) and must not be protocol-relative (//).
  // Decode first: a value like "/%2F%2Fevil.com" starts with a single "/"
  // and passes a naive string check, but browsers decode it to "//evil.com"
  // before navigating -- an open redirect. Reject on the decoded form too.
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return "/projects";
  }
  if (
    raw.startsWith("/") &&
    !raw.startsWith("//") &&
    !decoded.startsWith("//") &&
    !decoded.startsWith("/\\")
  ) {
    return raw;
  }
  return "/projects";
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}

function LoginForm() {
  const search = useSearchParams();
  const next = safeNext(search.get("next"));
  // Set by the email-verification redirect: "1" = confirmed, "invalid" = bad/
  // expired link.
  const verified = search.get("verified");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await postLogin({ email, password });
      // Full reload so middleware sees the new cookie. router.push won't
      // refresh the middleware-evaluated cookie state.
      window.location.href = next;
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-zinc-50 px-4 py-12">
      <div className="w-full max-w-sm rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-zinc-900">Sign in to Brandform</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Use the email and password tied to your workspace.
        </p>

        {verified === "1" && (
          <p className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            Email confirmed — you can sign in now.
          </p>
        )}
        {verified === "invalid" && (
          <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            That verification link is invalid or has expired. Sign in and we&apos;ll send a fresh one.
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
          />
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />

          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={pending}
            className="inline-flex h-10 w-full items-center justify-center rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-zinc-500">
          Don&apos;t have an account?{" "}
          <Link
            href={`/signup${next !== "/projects" ? `?next=${encodeURIComponent(next)}` : ""}`}
            className="font-medium text-blue-600 hover:underline"
          >
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  autoComplete,
  required,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-zinc-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
    </label>
  );
}
